import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action } from '@ngrx/store';
import { CartActions, CartConnector } from '@spartacus/cart/base/core'
import {
  GlobalMessageActions,
  GlobalMessageType,
  LoggerService,
  RoutingService,
  SiteContextActions,
  normalizeHttpError,
  withdrawOn
} from '@spartacus/core'
import { LaunchDialogService } from '@spartacus/storefront'
import {from, Observable} from 'rxjs'
import {catchError, concatMap, take, tap} from 'rxjs/operators'
import { EbAnalyticsService } from "../../../analytics/services/facades/eb-analytics.service"
import { EBCartModificationList } from '../../interface/eb-cart.interface'
import { EbOrderEntry } from "../../interface/eb-order-entry.interface";
import { EbCartAdapter } from '../../services/connectors/eb-cart.adapter'
import { EbActiveCartService } from "../../services/eb-active-cart.service";
import * as actions from '../actions'
import * as EbCartEntryAction from '../actions/eb-cart-entry.action'

@Injectable()
export class EbCartEffects {
  private readonly contextChange$ = this.actions$.pipe(
    ofType(SiteContextActions.CURRENCY_CHANGE, SiteContextActions.LANGUAGE_CHANGE)
  )

  protected logger = inject(LoggerService);
  cartEntries$: Observable<EbOrderEntry[]>;

  constructor(
    private actions$: Actions,
    private modalService: LaunchDialogService,
    private routingService: RoutingService,
    private cartConnector: CartConnector,
    private cartAdapter: EbCartAdapter,
    private analyticsService : EbAnalyticsService,
    private activeCartService: EbActiveCartService
  ) {
    this.cartEntries$ = this.activeCartService.getEntries();
  }

  csvUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.EB_CART_UPLOAD_CSV),
      concatMap((action: actions.EbCartCsvUpload) => {
        const { payload } = action
        return this.cartAdapter
          .uploadCsvFile(payload.cartId, payload.userId, payload.formData)
          .pipe(
            concatMap((cartModification: EBCartModificationList) => {
              const modificationActions: Action<any>[] = [
                new EbCartEntryAction.EbCartAddEntriesSuccess({
                  cartId: payload.cartId,
                  cartModification
                }),
                EbCartEntryAction.setCartModifications({
                  result: cartModification
                }),
                new CartActions.LoadCart({
                  cartId: payload.cartId,
                  userId: payload.userId
                })
              ]
              cartModification?.cartModifications.forEach(value => {
                if (value.statusCode === 'maxOrderQuantityExceeded') {
                  modificationActions.push(
                    new GlobalMessageActions.AddMessage({
                      text: {
                        key: 'addToCart.maxOrderQuantityExceeded'
                      },
                      type: GlobalMessageType.MSG_TYPE_WARNING,
                      timeout: Number.MAX_VALUE
                    })
                  )
                }
                this.cartEntries$.pipe(take(1)).subscribe(entries => this.analyticsService.addEcommerceProductToCart(entries, value.entry.product.code, 'Ecommerce', 'Quick-add-to-cart', 'CSV import'))
              })
              return modificationActions
            }),
            tap(() => this.modalService.closeDialog('CLOSE')),
            catchError(error => {
              this.modalService.closeDialog('CLOSE')
              const errorType = error?.error?.errors?.[0]?.type
              const errorText =
                errorType === 'InvalidFileError'
                  ? {
                      key: `csv.${errorType}`
                    }
                  : {
                      key: 'csv.otherError',
                      params: { errorMessage: error?.error?.errors?.[0]?.message }
                    }
              return from([
                new GlobalMessageActions.AddMessage({
                  text: errorText,
                  type: GlobalMessageType.MSG_TYPE_ERROR,
                  timeout: Number.MAX_VALUE
                }),
                new EbCartEntryAction.EbCartAddEntriesFail({
                  cartId: payload.cartId,
                  error: normalizeHttpError(error, this.logger)
                }),
                new CartActions.LoadCart({
                  cartId: payload.cartId,
                  userId: payload.userId
                })
              ])
            })
          )
      }),
      withdrawOn(this.contextChange$)
    )
  )
}
